<template>
  <page-loader>
    <v-container fluid class="container">
      <v-form ref="form">
        <v-row>
          <v-col class="d-flex align-center">
            <v-breadcrumbs :items="breadcrumbs" class="px-0 py-0" />
            <v-spacer />
            <div class="d-inline-block text-right">
              <v-tooltip max-width="300" bottom>
                <template #activator="{ on }">
                  <base-chip
                    v-show="hasChanges"
                    color="blue"
                    text="Unsaved changes detected"
                    icon="info"
                    dark
                    small
                    v-on="on"
                  >
                  </base-chip>
                </template>
                <span>Remember to save your changes before leaving!</span>
              </v-tooltip>
              <published-chip :value="original.wp_publish_status" class="ml-4 d-inline-block" />
              <div class="text-caption font-italic mt-2">{{ formatLastUpdated(original.updated_at) }}</div>
            </div>
          </v-col>
        </v-row>

        <v-divider class="mt-1 mb-4" />

        <v-row>
          <v-col cols="8">
            <v-alert v-if="hasDuplicateSlug" type="warning" class="mb-6">{{ duplicateMessage }}</v-alert>
            <v-alert v-if="formData.wp_publish_status !== 'draft' && !canPublish" type="error" class="mb-6">{{
              publishMessage
            }}</v-alert>
            <v-alert v-if="publishedDateInFuture" type="error" class="mb-6">{{ futureMessage }}</v-alert>

            <v-card
              :flat="!showRevisionTabs"
              :outlined="showRevisionTabs"
              :color="showRevisionTabs ? null : 'transparent'"
            >
              <template v-if="showRevisionTabs">
                <div class="d-flex align-center pa-4 grey lighten-4">
                  <v-tooltip top>
                    <template #activator="{ on }">
                      <div class="d-inline-block mr-4" v-on="on">
                        <v-btn
                          :disabled="hasChanges || isReadOnly"
                          color="primary"
                          depressed
                          small
                          @click="onOverwriteClick"
                        >
                          <span>Revision</span>
                          <v-icon>double_arrow</v-icon>
                          <span>Current</span>
                        </v-btn>
                      </div>
                    </template>
                    <span v-if="isReadOnly">User does not have update permissions!</span>
                    <span v-else-if="!hasChanges">Overwrite current content with revision changes</span>
                    <span v-else>Page contains changes that should be saved or discarded first!</span>
                  </v-tooltip>
                  <v-tooltip top>
                    <template #activator="{ on }">
                      <div class="d-inline-block mr-4" v-on="hasChanges || isReadOnly ? on : null">
                        <v-btn
                          :disabled="hasChanges || isReadOnly"
                          color="error"
                          depressed
                          small
                          @click="showClearRevisionModal = true"
                        >
                          Clear Revised Changes
                        </v-btn>
                      </div>
                    </template>
                    <span v-if="isReadOnly">User does not have update permissions!</span>
                    <span v-else>Page contains changes that should be saved or discarded first!</span>
                  </v-tooltip>
                </div>

                <v-tabs v-model="revisionTab" grow slider-size="3" height="58">
                  <v-tab v-for="{ label, tabTooltip } in revisionTabOptions" :key="label">
                    <span>{{ label }}</span>
                    <v-tooltip max-width="300" bottom>
                      <template #activator="{ on }">
                        <v-icon v-if="tabTooltip" class="ml-2" small v-on="on">info</v-icon>
                      </template>
                      {{ tabTooltip }}
                    </v-tooltip>
                  </v-tab>
                </v-tabs>
              </template>

              <v-card-text :class="{ 'pa-0': !showRevisionTabs }">
                <v-tabs-items v-model="revisionTab" style="background-color: transparent">
                  <v-tab-item
                    v-for="{ legacyModel, contentModel, metaModel, readonly, showTooltips } in revisionTabOptions"
                    :key="contentModel"
                    class="pt-4"
                  >
                    <text-input
                      v-model="formData[metaModel].headline"
                      :readonly="readonly"
                      :required="!readonly"
                      class="mb-4"
                      hint="This title will be used to generate a slug if the 'Slug' field in the Publish section is left blank."
                      label="Page Title *"
                      outlined
                      persistent-hint
                    />

                    <v-card outlined>
                      <v-expansion-panels flat>
                        <v-expansion-panel>
                          <v-expansion-panel-header color="grey lighten-4">Meta Details</v-expansion-panel-header>
                          <v-expansion-panel-content>
                            <text-input
                              v-model="formData[metaModel].seo_title"
                              :readonly="readonly"
                              label="Meta Title"
                              hint="For best results, it's recommended for this to be less than 60 characters."
                              class="my-6"
                              persistent-hint
                              counter
                            />

                            <text-input
                              v-model="formData[metaModel].seo_description"
                              :readonly="readonly"
                              label="Meta Description"
                              hint="For best results, it's recommended for this to be less than 155 characters."
                              class="mb-6"
                              persistent-hint
                              counter
                            />

                            <text-input
                              v-model="formData[metaModel].seo_excerpt"
                              :readonly="readonly"
                              label="Hub Excerpt"
                              hint="For best results, it's recommended for this to be less than 155 characters."
                              class="mb-6"
                              persistent-hint
                              counter
                            />
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                      </v-expansion-panels>
                    </v-card>

                    <v-card class="mt-6" outlined>
                      <v-card-subtitle class="d-flex align-center mb-2 grey lighten-4 pa-3">
                        <span>Content</span>
                        <v-spacer />
                        <toggle-input
                          v-model="formData.use_legacy_editor"
                          :is-disabled="readonly"
                          label="Legacy Editor"
                          class="mr-4"
                        />
                        <toggle-input v-model="expanded" :label="expandedLabel" />
                      </v-card-subtitle>

                      <v-froala-input
                        v-if="formData.use_legacy_editor"
                        v-model="formData[legacyModel]"
                        :readonly="readonly"
                        :toolbar-buttons="froalaToolbarButtons"
                        class="px-4 py-4"
                      />

                      <template v-else>
                        <v-expansion-panels v-model="panels" multiple accordion>
                          <v-expansion-panel
                            v-for="(module, index) in filteredModules"
                            :key="index"
                            :readonly="(module.fields && module.fields.length === 0) || !module.fields"
                          >
                            <v-expansion-panel-header>
                              <div class="justify-between d-flex align-center">
                                <v-tooltip bottom small>
                                  <template #activator="{ on }">
                                    <div v-on="on">
                                      <toggle-input
                                        v-model="formData[contentModel][module.key].visible"
                                        label=""
                                        :is-disabled="readonly"
                                        class="mr-4"
                                        @click.native.stop
                                      />
                                    </div>
                                  </template>
                                  <span>Toggle module visibility</span>
                                </v-tooltip>
                                <span class="mr-4">{{ module.title || module.name }}</span>
                                <base-chip :text="`#${module.key}`" class="mr-4" dark x-small />
                                <div v-if="showTooltips" class="ml-auto">
                                  <v-tooltip
                                    v-if="hasEmptyRequiredField(module) && formData.content[module.key].visible"
                                    bottom
                                    small
                                  >
                                    <template #activator="{ on }">
                                      <v-icon color="error" class="mr-4" v-on="on">info</v-icon>
                                    </template>
                                    <span>This module has required content that is empty</span>
                                  </v-tooltip>
                                  <v-tooltip v-if="hasPendingChanges(module)" bottom small>
                                    <template #activator="{ on }">
                                      <v-icon color="warning" class="mr-4" v-on="on">info</v-icon>
                                    </template>
                                    <span>This module has revised content</span>
                                  </v-tooltip>
                                </div>
                              </div>
                              <template #actions>
                                <p v-if="(module.fields && module.fields.length === 0) || !module.fields" />
                              </template>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content>
                              <page-module-editor
                                v-for="(field, key) in module.fields"
                                :key="key"
                                v-model="formData[contentModel][module.key][field.key]"
                                :field="field"
                                :type="field.type"
                                :label="field.title"
                                :module="module"
                                :readonly="readonly"
                                :select-options="selectOptions"
                                :disabled="!formData[contentModel][module.key].visible"
                                class="mt-6"
                              />
                            </v-expansion-panel-content>
                          </v-expansion-panel>
                        </v-expansion-panels>
                      </template>
                    </v-card>
                  </v-tab-item>
                </v-tabs-items>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="4">
            <v-card outlined class="mb-6">
              <v-card-subtitle class="mb-2 grey lighten-4 pa-3">Details</v-card-subtitle>
              <v-card-text class="py-4">
                <select-list
                  v-model="formData.site_id"
                  :items="siteOptions"
                  label="Site *"
                  hide-details
                  class="mb-6"
                  disabled
                />
                <select-list
                  v-model="formData.language_id"
                  :items="languageOptions"
                  label="Language *"
                  hide-details
                  class="mb-6"
                  disabled
                />
                <select-list
                  v-model="formData.page_type_id"
                  :items="pageTypeOptions"
                  label="Page Type *"
                  hide-details
                  class="mb-6"
                  disabled
                />
                <select-list
                  v-model="formData.author_id"
                  :items="authorOptions"
                  :readonly="isReadOnly"
                  label="Author *"
                  hide-details="auto"
                  class="mb-6"
                  required
                />

                <select-list
                  v-model="formData.wp_parent_post_id"
                  :items="pageOptions"
                  :required="formData.wp_parent_post_id !== 0"
                  :readonly="isReadOnly"
                  label="Parent Page *"
                  hide-details
                  class="mb-6"
                />
                <select-list
                  v-for="(templateOption, index) in formData.pageType.template_options"
                  :key="index"
                  v-model="formData.template_options[templateOption.template_option]"
                  :items="templateOption.options"
                  :label="templateOption.template_option | cleanText"
                  :readonly="isReadOnly"
                  hide-details
                  class="mb-6"
                />
              </v-card-text>
            </v-card>

            <v-card outlined class="mb-6">
              <v-card-subtitle class="mb-2 grey lighten-4 pa-3">Publish</v-card-subtitle>
              <v-card-text>
                <select-list
                  v-model="formData.wp_publish_status"
                  :items="wpPublishStatuses"
                  :readonly="isReadOnly"
                  class="mt-6"
                  label="Meta Status *"
                  hide-details
                  required
                />
                <date-time-picker
                  v-model="formData.published_at"
                  :clear-value="null"
                  :readonly="isReadOnly"
                  :text-field-props="{ class: 'mt-6' }"
                  label="Published at"
                  hide-details
                />
                <date-time-picker
                  v-model="formData.published_updated_at"
                  :text-field-props="{ class: 'mt-6' }"
                  :clear-value="null"
                  :readonly="isReadOnly"
                  label="Last published"
                  disabled
                  hide-details
                />
                <text-input
                  v-model="formData.wp_slug"
                  :readonly="isReadOnly"
                  type="slug"
                  label="Slug"
                  hint="URL pathname for article. Only letters, numbers and hyphens allowed. If unset, will automatically generate from article title."
                  class="mt-6"
                  persistent-hint
                  counter
                />

                <text-input
                  v-model="formData.wp_post_id"
                  :disabled="!hasRole('admin', 'wordpress-admin')"
                  :readonly="isReadOnly"
                  class="mt-6"
                  type="number"
                  label="Post ID"
                />
              </v-card-text>
            </v-card>

            <v-card v-if="formData.wp_permalink" outlined class="mb-6">
              <v-card-subtitle class="mb-4 grey lighten-4 pa-3">Page Preview</v-card-subtitle>
              <v-card-text>
                <v-btn
                  v-if="isUserInDepartment('tech')"
                  :href="debugUrl"
                  color="info"
                  target="_blank"
                  title="Debug Response"
                  class="mb-2"
                  block
                  depressed
                >
                  <v-icon class="mr-2" small>code</v-icon>
                  <span>Debug API response</span>
                </v-btn>
                <v-btn
                  v-if="formData.wp_permalink"
                  color="primary"
                  title="Preview in new tab"
                  class="mb-6"
                  block
                  depressed
                  @click="onPermalinkOpen"
                >
                  <v-icon class="mr-2" small>launch</v-icon>
                  <span>Open in new tab</span>
                </v-btn>

                <text-input
                  v-if="formData.wp_permalink"
                  v-model="formData.wp_permalink"
                  label="WP Permalink"
                  prepend-icon="content_copy"
                  readonly
                  @click:prepend="onPermalinkCopy"
                />
              </v-card-text>
            </v-card>

            <v-card outlined class="mb-6">
              <v-card-subtitle class="mb-2 grey lighten-4 pa-3">SEO</v-card-subtitle>
              <v-card-text>
                <text-input
                  v-model="formData.wp_meta_seo_canonical"
                  :readonly="isReadOnly"
                  label="SEO Canonical"
                  class="mt-6"
                  type="url"
                  hide-details
                  clearable
                  counter
                />

                <toggle-input
                  v-model="formData.wp_meta_seo_noindex"
                  :readonly="isReadOnly"
                  label="SEO No Index"
                  class="mt-6"
                />
                <toggle-input
                  v-model="formData.wp_meta_seo_nofollow"
                  :readonly="isReadOnly"
                  label="SEO No Follow"
                  class="mt-6"
                />
              </v-card-text>
            </v-card>

            <page-entity-list
              v-if="formData.entities && formData.entities.length"
              :entities="formData.entities"
              :entity-types="entityTypes"
              class="mb-6"
            />

            <page-language-list
              :available-languages="availableLanguageSites"
              :has-changes="hasChanges"
              :page-loading="similarPagesLoading"
              :pages="similarPages"
              :parent-page="formData"
              :can-update="isPermittedTo('update')"
              class="mb-16"
              @refresh="refresh(false)"
            />
          </v-col>
        </v-row>
      </v-form>
    </v-container>

    <!-- Delete Modal -->
    <delete-modal
      v-if="deleteModalVisible"
      v-model="deleteModalVisible"
      category="Page"
      :item="formData.meta.headline || title || 'this page'"
      @modal-confirm="deletePage"
    />

    <!-- Help Modal -->
    <generic-modal v-model="showHelpModal" title="Page Editor Guide" hide-buttons hide-louis width="900">
      <div class="text-h6">Content/Meta Changes</div>
      <p>
        By default, content changes in the left column (Content/Meta) will not overwrite existing content, or will not
        be pushed to a live page if published. Changes will be saved in Console under the Revisions tab.
      </p>
      <div class="text-caption font-weight-bold">Overwriting with Unsaved Changes</div>
      <p>
        If the page contains unsaved changes, you can overwrite the current draft by clicking the toggle in the Save
        modal. If the page is published, this will push the revised changes to the live page.
      </p>
      <div class="text-caption font-weight-bold">Overwriting with Saved Changes</div>
      <p>
        If the page has no unsaved changes, you can overwrite the current draft with revised changes, or push revised
        changes live if the page is published by clicking the '<b>Revision &gt;&gt; Current</b>' button above the
        Content/Meta tabs.
      </p>
      <div class="text-caption font-weight-bold">Other Changes</div>
      <p>
        Changes to the right column (not Content/Meta) are not revised and will affect live pages if published.
      </p>

      <div class="text-h6">Use Case Matrix</div>

      <v-data-table :headers="helpTableHeaders" :items="helpTableItems" disable-sort hide-default-footer>
        <template v-slot:header>
          <thead>
            <tr>
              <th colspan="3">Input</th>
              <th colspan="3">Output</th>
            </tr>
          </thead>
        </template>
        <template #item.status="{ value }">
          <published-chip :value="value" small />
        </template>
        <template #item.revised="{ value }">
          <v-icon :color="value ? 'success' : 'error'">{{ value ? 'check_circle' : 'cancel' }}</v-icon>
        </template>
        <template #item.overwrite="{ value }">
          <span v-if="value === null">-</span>
          <v-icon v-else :color="value ? 'success' : 'error'">{{ value ? 'check_circle' : 'cancel' }}</v-icon>
        </template>
        <template #item.savedRevised="{ value }">
          <v-icon :color="value ? 'success' : 'error'">{{ value ? 'check_circle' : 'cancel' }}</v-icon>
        </template>
        <template #item.savedStatus="{ value }">
          <published-chip :value="value" small />
        </template>
      </v-data-table>

      <div class="text-h6 mt-6">Translations</div>

      <p>
        When a page is translated, the new content will be saved under the Revisions tab and will not overwrite the
        <b>current</b> content. However, if the page already had <b>revised</b> content, this will be overwritten by the
        newly translated content.
      </p>

      <div class="text-h6">Academy Docs</div>
      <ul>
        <li>
          <a href="https://academy.investoogroup.com/wiki/how-to-make-revisions-to-pages-in-console/" target="_blank"
            >Revisions</a
          >
        </li>
        <li>
          <a href="https://academy.investoogroup.com/wiki/how-to-translate-pages-in-console/" target="_blank"
            >Translating</a
          >
        </li>
      </ul>
    </generic-modal>

    <!-- Reset unsaved-changes Modal -->
    <reset-modal
      v-model="showResetModal"
      :is-disabled="loading"
      :is-loading="loading"
      category="page"
      @modal-confirm="onResetConfirm"
    />

    <!-- Save Modal -->
    <generic-modal
      v-model="showSaveModal"
      :is-loading="loading"
      hide-louis
      persistent
      @modal-confirm="savePage"
      @modal-cancel="onSaveModalCancel"
    >
      <template #title>
        <div class="d-flex justify-space-between flex-grow-1">
          <div>{{ saveModalTitle }}</div>
          <!-- Status Update -->
          <div v-if="formData.wp_publish_status !== original.wp_publish_status" class="d-flex align-center">
            <published-chip :value="original.wp_publish_status" small />
            <v-icon class="mx-2">double_arrow</v-icon>
            <published-chip :value="formData.wp_publish_status" small />
          </div>
        </div>
      </template>
      <v-container>
        <!-- Messages -->
        <v-row>
          <v-col>
            <!-- Toggle On Message -->
            <div v-if="syncRevisions">
              <template v-if="isSettingToDraft">
                Are you sure you want to <b>overwrite</b> the current version of this page with the revised changes?
                This cannot be undone.
              </template>
              <template v-else>
                Are you sure you want to <b>overwrite</b> the current version and push revised changes live? This cannot
                be undone.
              </template>
            </div>

            <!-- Toggle Off Message -->
            <template v-else>
              <div v-if="!isDraft && !isSettingToDraft">
                <div>This is a <b>live</b> page. Are you sure you want to save these changes?</div>
                <div class="mt-4">
                  Content and Meta updates will be saved as revised changes but will not affect the live page. However,
                  any other changes made will be pushed live.
                </div>
              </div>
              <div v-else>
                Are you sure you want to save these changes?
              </div>
            </template>
          </v-col>
        </v-row>

        <!-- Overwrite Toggle -->
        <v-row v-if="showOverwriteSaveToggle">
          <v-col>
            <v-divider class="mb-6" />

            <div class="d-flex align-center">
              <toggle-input
                v-model="syncRevisions"
                :label="isSettingToDraft ? 'Overwrite Current Version' : 'Publish Revised Changes'"
                class="mr-4"
              />
              <v-tooltip right max-width="300">
                <template #activator="{ on }">
                  <v-icon v-on="on">info</v-icon>
                </template>
                <span
                  >Revision changes will be saved but will only overwrite current content if this toggle is
                  active.</span
                >
              </v-tooltip>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </generic-modal>

    <!-- Overwrite Modal -->
    <generic-modal
      v-model="showOverwriteModal"
      :confirm-text="overwriteModalConfirmText"
      :is-loading="loading"
      persistent
      title="Overwrite Current Content"
      hide-louis
      @modal-confirm="savePage"
      @modal-cancel="onSaveModalCancel"
    >
      <template v-if="isSettingToDraft">
        Are you sure you want to <b>overwrite</b> the current version of this page with the revised changes? This cannot
        be undone.
      </template>
      <template v-else>
        Are you sure you want to <b>overwrite</b> the current version and push revised changes live? This cannot be
        undone.
      </template>
    </generic-modal>

    <!-- Clear Revisions Modal -->
    <generic-modal
      v-model="showClearRevisionModal"
      title="Clear Revised Changes"
      hide-louis
      @modal-confirm="onClearRevisionConfirm"
    >
      Are you sure you want to <span class="font-weight-bold red--text">REMOVE</span> revised changes? This cannot be
      undone.
    </generic-modal>

    <fab-actions
      :can-delete="isPermittedTo('delete')"
      :can-edit="isPermittedTo('update')"
      :has-changes="hasChanges"
      context="Page"
      @delete="toggleDeleteModal"
      @reset="showResetModal = true"
      @save="showSaveModal = true"
      @help="showHelpModal = true"
    />

    <!-- Exit Page Modal -->
    <exit-modal
      :is-disabled="loading"
      :is-loading="loading"
      :value="showExitModal"
      @modal-confirm="confirmExitBeforeSave"
      @modal-cancel="cancelExitBeforeSave"
    />
  </page-loader>
</template>

<script>
// Helpers
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { cloneDeep, isEqual, get } from 'lodash-es'
import moment from 'moment'
import { formatLastUpdated } from '@/utils/date'

// Mixins
import PermissionsHandler from '@/mixins/permissions-handler'
import DataModification from '@/mixins/data-modification'
import RedirectHandler from '@/mixins/redirect-handler'

// Components
import BaseChip from '@/components/chips/base-chip'
import DateTimePicker from '@/components/datetime-picker'
import DeleteModal from '@/components/modals/delete-modal'
import ExitModal from '@/components/modals/exit-modal'
import FabActions from '@/components/fab-actions'
import GenericModal from '@/components/modals/generic-modal'
import PageModuleEditor from './components/page-module-editor'
import PageLanguageList from './components/page-language-list'
import PageEntityList from './components/page-entity-list'
import PageLoader from '@/components/page-loader.vue'
import PublishedChip from '@/views/news-articles/components/published-chip'
import ResetModal from '@/components/modals/reset-modal'
import SelectList from '@/components/forms/select-list'
import TextInput from '@/components/forms/text-input'
import ToggleInput from '@/components/forms/toggle-input'
import VFroalaInput from '@/components/forms/froala-input'

// Services
import { pages, wordpress, authors, entityTypes } from '@/services/offers'

const blankFormData = () => ({
  author_id: null,
  content: {},
  entities: [],
  id: null,
  language: {},
  language_id: null,
  legacy_content: null,
  meta: {},
  page_type_id: null,
  pageType: {},
  published_at: null,
  published_content: {},
  published_legacy_content: null,
  published_meta: {},
  published_updated_at: null,
  seo_approved: false,
  site: {},
  site_id: null,
  template_options: {},
  use_legacy_editor: false,
  uuid: null,
  wp_hub_excerpt: null,
  wp_meta_seo_canonical: null,
  wp_meta_seo_noindex: false,
  wp_meta_seo_nofollow: false,
  wp_parent_post_id: 0,
  wp_permalink: null,
  wp_post_id: null,
  wp_publish_status: 'draft',
  wp_slug: null,
})

export default {
  name: 'PagesEdit',
  components: {
    BaseChip,
    DateTimePicker,
    DeleteModal,
    ExitModal,
    FabActions,
    GenericModal,
    PageLoader,
    PageModuleEditor,
    PageLanguageList,
    PageEntityList,
    PublishedChip,
    ResetModal,
    SelectList,
    TextInput,
    ToggleInput,
    VFroalaInput,
  },
  filters: {
    cleanText(val) {
      return val.replace(/_/g, ' ').replace(/(^\w|\s\w)/g, m => m.toUpperCase())
    },
  },
  mixins: [DataModification, PermissionsHandler, RedirectHandler],
  data() {
    return {
      revisionTab: 0,
      expanded: true,
      loading: false,
      deleteModalVisible: false,
      duplicateMessage: 'This page may be a duplicate',
      futureMessage: 'This page is scheduled to be posted in the future',
      publishMessage: 'You cannot publish this page until you have filled out all required fields, headline and author',
      panels: [],
      pages: [],
      entityTypes: [],
      similarPages: [],
      authors: [],
      original: {},
      options: { authors: [] },
      showHelpModal: false,
      showResetModal: false,
      showClearRevisionModal: false,
      showSaveModal: false,
      showOverwriteModal: false,
      similarPagesLoading: false,
      syncRevisions: false,
      formData: blankFormData(),
      helpTableHeaders: [
        { text: 'Status', value: 'status' },
        { text: 'Has Revision', value: 'revised', align: 'center' },
        { text: 'Overwrite Content', value: 'overwrite', align: 'center' },
        { text: 'Status', value: 'savedStatus' },
        { text: 'Has Revision', value: 'savedRevised', align: 'center' },
        { text: 'Outcome', value: 'outcome' },
      ],
      helpTableItems: [
        {
          status: 'draft',
          revised: false,
          overwrite: null,
          savedStatus: 'draft',
          savedRevised: false,
          outcome: 'No content revisions made. Any other changes (e.g, author) are saved.',
        },
        {
          status: 'draft',
          revised: true,
          overwrite: false,
          savedStatus: 'draft',
          savedRevised: true,
          outcome: 'Changes saved under Revision tab, current content is unchanged.',
        },
        {
          status: 'draft',
          revised: true,
          overwrite: true,
          savedStatus: 'draft',
          savedRevised: false,
          outcome: 'Revision changes will overwrite existing content.',
        },
        {
          status: 'draft',
          revised: true,
          overwrite: true,
          savedStatus: 'publish',
          savedRevised: false,
          outcome: 'Revision changes will overwrite existing content and this new content will be published.',
        },
        {
          status: 'publish',
          revised: false,
          overwrite: null,
          savedStatus: 'publish',
          savedRevised: false,
          outcome: 'No content revisions made. Any other changes (e.g, author) are pushed live.',
        },
        {
          status: 'publish',
          revised: true,
          overwrite: false,
          savedStatus: 'publish',
          savedRevised: true,
          outcome: 'Changes saved under Revision tab, current content is unchanged.',
        },
        {
          status: 'publish',
          revised: true,
          overwrite: true,
          savedStatus: 'publish',
          savedRevised: false,
          outcome: 'Revision changes will overwrite current content and be pushed live.',
        },
        {
          status: 'publish',
          revised: true,
          overwrite: false,
          savedStatus: 'draft',
          savedRevised: true,
          outcome:
            'Page will be unpublished. Published content will remain as current content. Revised changes will appear under Revision tab.',
        },
      ],
    }
  },
  computed: {
    ...mapGetters({
      isUserInDepartment: 'account/isUserInDepartment',
      modules: 'modules/getModules',
      getItemsForOption: 'options/getItemsForOption',
      sites: 'sites/getSites',
      languages: 'languages/getLanguages',
      languageSites: 'languageSites/getLanguageSites',
      pageTypes: 'pageTypes/getPageTypes',
    }),
    expandedLabel() {
      return this.expanded ? 'Collapse all' : 'Expand all'
    },
    revisionTabOptions() {
      return [
        {
          label: 'Revision',
          contentModel: 'content',
          legacyModel: 'legacy_content',
          metaModel: 'meta',
          readonly: this.isReadOnly,
          showTooltips: true,
          tabTooltip: this.isDraft
            ? 'These changes are saved in console but will not overwrite current content. Click the toggle in the Save modal to overwrite with revised changes.'
            : 'These changes are saved in console but are not live. Click the toggle in the Save modal to push these changes live.',
        },
        {
          label: 'Current (Read Only)',
          contentModel: 'published_content',
          legacyModel: 'published_legacy_content',
          metaModel: 'published_meta',
          readonly: true,
          showTooltips: false,
          tabTooltip: false,
        },
      ]
    },
    isDraft() {
      return this.original.wp_publish_status === 'draft'
    },
    isPrivate() {
      return this.original.wp_publish_status === 'private'
    },
    isPublished() {
      return this.original.wp_publish_status === 'publish'
    },
    isRevised() {
      return !!this.original.is_revised
    },
    isReadOnly() {
      return !this.isPermittedTo('update')
    },
    isSettingToDraft() {
      return this.formData.wp_publish_status === 'draft'
    },
    showRevisionTabs() {
      return this.isRevised
    },
    publishedDateInFuture() {
      if (!this.formData.published_at) return false
      return moment().diff(this.formData.published_at, 'days') < 0
    },
    hasDuplicateSlug() {
      return /(.*)-(\d+)$/gm.test(this.formData.wp_slug)
    },
    canPublish() {
      if (!this.formData.meta.headline) return false
      if (!this.formData.author_id) return false
      if (!this.formData.wp_publish_status) return false
      return this.formData.wp_parent_post_id !== null
    },
    title() {
      if (this.formData.meta.headline) return this.formData.meta.headline
      return this.formData.entities.map(({ entity: { default: { name } } = {} }) => name).join(' ')
    },
    breadcrumbs() {
      return [
        { text: 'Pages', to: { name: 'content.pages' }, exact: true },
        { text: this.title || 'loading', disabled: true },
      ]
    },
    availableLanguageSites() {
      const { site: { site_group_id } = {} } = this.formData
      return this.languageSites.data.filter(({ site: { site_group_id: siteGroupId } }) => siteGroupId === site_group_id)
    },
    filteredModules() {
      if (!this.formData.pageType.schema) return []
      return Object.values(this.formData.pageType.schema.sections)
        .flatMap(modules => modules)
        .filter(({ id }) => !!this.modules.data.find(({ id: moduleId }) => moduleId === id))
        .filter(({ template_options }) =>
          template_options.every(({ value, template_option }) => {
            const defaultValue = get(
              this.formData.template_options,
              template_option,
              get(templateOption, 'default_option', null)
            )
            const templateOption =
              this.formData.pageType.template_options.find(({ template_option: to }) => to === template_option) || {}
            return value == defaultValue
          })
        )
        .map(({ id, template_options, key, type }) => ({
          ...this.modules.data.find(({ id: moduleId }) => moduleId === id),
          template_options,
          type,
          key,
        }))
    },
    froalaToolbarButtons() {
      return () => [
        'paragraphFormat',
        'align',
        'bold',
        'underline',
        'italic',
        'strikeThrough',
        'clearFormatting',
        'insertTable',
        'insertVideo',
        'formatOL',
        'formatUL',
        'mediaGallery',
        'customLinkInsert',
        'shortcodeInsert',
        'html',
      ]
    },
    hasChanges() {
      return !isEqual(this.original, this.formData)
    },
    authorOptions() {
      return this.authors
        .filter(({ author }) => author.some(({ site_group_id }) => site_group_id === this.formData.site.site_group_id))
        .map(({ id: value, first_name, last_name }) => ({ key: `${first_name} ${last_name}`.trim(), value }))
    },
    siteOptions() {
      return this.sites.data.map(({ id: value, hostname: key }) => ({ key, value }))
    },
    languageOptions() {
      return this.languages.data.map(({ id: value, name: key }) => ({ key, value }))
    },
    pageTypeOptions() {
      return this.pageTypes.data.map(({ id: value, name: key }) => ({ key, value }))
    },
    pageOptions() {
      return [{ value: 0, key: 'No parent' }, ...this.recursivelyFlattenPages(this.pages)]
    },
    wpPublishStatuses() {
      return this.getItemsForOption('wordpressStatuses')
    },
    categoryOptions() {
      return get(this.options, 'categories', [])
        .filter(({ language }) => language === this.formData.language.code)
        .map(({ name: text, id: value }) => ({ text, value }))
    },
    moduleOptions() {
      return this.filteredModules.map(({ key: value }) => ({ text: `#${value}`, value }))
    },
    selectOptions() {
      return {
        categories: this.categoryOptions,
        modules: this.moduleOptions,
        // @note - to add more dynamic select options add the dataset here...
      }
    },
    overwriteModalConfirmText() {
      return this.isSettingToDraft ? 'Overwrite Current Version' : 'Publish Revised Changes'
    },
    saveModalTitle() {
      return this.hasChanges ? 'Save Changes' : 'Overwrite Current Version'
    },
    showOverwriteSaveToggle() {
      return (
        this.formData.is_revised ||
        !isEqual(this.original.content, this.formData.content) ||
        !isEqual(this.original.meta, this.formData.meta) ||
        !isEqual(this.original.legacy_content, this.formData.legacy_content)
      )
    },
    debugUrl() {
      return `${process.env.VUE_APP_OFFERS_API_ENDPOINT}public/organic/pages/${this.original.uuid}`
    },
  },
  watch: {
    expanded(value) {
      this.setDefaultPanels()
    },
    'formData.template_options': {
      immediate: true,
      deep: true,
      handler() {
        this.formData.content = this.mapContent(this.formData.content)
        this.formData.published_content = this.mapContent(this.formData.published_content)
      },
    },
    'formData.wp_publish_status'(newStatus) {
      if (this.original.wp_publish_status !== 'publish') return
      if (newStatus === 'publish') return

      this.$notify({
        type: 'warning',
        text: `Warning! You are unpublishing this page from '${this.original.site.name}'. This will result in 404 error if a user attempts to visit the page.`,
        ignoreDuplicates: true,
        duration: -1,
      })
    },
  },
  async created() {
    await this.refresh()
    this.setBeforeUnloadListener(this.onBeforeUnload)
  },
  beforeRouteLeave(to, from, next) {
    if (!this.hasChanges) return next()
    this.handleExitBeforeSave(next)
  },
  beforeDestroy() {
    this.removeBeforeUnloadListener(this.onBeforeUnload)
  },
  methods: {
    ...mapActions({
      fetchModules: 'modules/fetch',
      fetchSites: 'sites/fetch',
      fetchLanguages: 'languages/fetch',
      fetchLanguageSites: 'languageSites/fetch',
      fetchPageTypes: 'pageTypes/fetch',
    }),
    ...mapMutations({
      setPageLoading: 'general/setPageLoading',
    }),
    formatLastUpdated,
    toggleDeleteModal() {
      this.deleteModalVisible = !this.deleteModalVisible
    },
    hasEmptyRequiredField(module) {
      if (!module.fields || module.fields.length === 0) return false
      return module.fields.every(({ required: fieldIsRequired, key: fieldKey }) => {
        if (!fieldIsRequired) return false
        if (!this.formData.content[module.key]) return true
        return !this.formData.content[module.key][fieldKey]
      })
    },
    hasPendingChanges(module) {
      if (!module.fields.length) return false
      return (
        !module.fields.every(field =>
          isEqual(
            get(this.formData.content, `${module.key}.${field.key}`),
            get(this.formData.published_content, `${module.key}.${field.key}`)
          )
        ) || !isEqual(this.formData.content[module.key].visible, this.formData.published_content[module.key].visible)
      )
    },
    mapContent(content) {
      return this.filteredModules.reduce((previousValue, currentValue) => {
        previousValue[currentValue.key] = { module: currentValue.name }
        previousValue[currentValue.key].visible = get(content, `${currentValue.key}.visible`, true)

        for (const field of currentValue.fields) {
          previousValue[currentValue.key][field.key] = get(
            content,
            `${currentValue.key}.${field.key}`,
            field.type === 'flexible' ? [] : null
          )
        }
        return previousValue
      }, {})
    },
    recursivelyFlattenPages(pages = [], parentTitle = null) {
      const items = []
      for (const page of pages) {
        const title = page.title || `#${page.id} (no title)`
        const titleCombination = parentTitle ? `${parentTitle} / ${title}` : title
        if (page.language.content != this.formData.language.code) continue
        items.push({ value: page.id, key: titleCombination })
        items.push(...this.recursivelyFlattenPages(page.children, titleCombination))
      }
      return items
    },
    onPermalinkOpen() {
      window.open(this.formData.wp_permalink, '_blank').focus()
    },
    onPermalinkCopy() {
      try {
        navigator.clipboard.writeText(this.formData.wp_permalink)
        this.$notify({
          type: 'info',
          text: 'Copied page URL to clipboard',
        })
      } catch (e) {
        this.$notify({
          type: 'error',
          text: 'Unable to copy page URL to clipboard',
        })
      }
    },
    fetchWordpressData() {
      wordpress.getOptions(this.formData.site_id).then(options => (this.options = options))
      wordpress.getPages(this.formData.site_id, this.formData.language.code).then(wpPages => (this.pages = wpPages))
    },
    async fetchEntityTypes() {
      const entityTypeIds = this.formData.entities.map(({ entity_type_id }) => entity_type_id)
      const { data } = await entityTypes.getEntityTypes({
        filters: { idIn: entityTypeIds },
        paginate: { perPage: null },
      })
      this.entityTypes = data
    },
    async fetchAuthors() {
      const { data } = await authors.getAll({ paginate: { perPage: null } })
      this.authors = data
    },
    async fetchSimilarPages() {
      this.similarPagesLoading = true
      const { page_type_id, entities, site: { site_group_id } = {} } = this.formData
      const paginate = { perPage: null }
      const filters = { page_type_id }
      const { data } = await pages.getAll({ filters, paginate })
      const entityIds = entities.map(({ id }) => id)
      this.similarPages = data
        .filter(({ site: { site_group_id: siteGroupId } }) => siteGroupId === site_group_id)
        .filter(page => page.entities.map(({ id }) => id).every(entityId => entityIds.includes(entityId)))
      this.similarPagesLoading = false
    },
    async refresh(originalLoadingState = true) {
      try {
        this.setPageLoading(originalLoadingState)
        const paginate = { perPage: null }
        await Promise.all([
          this.fetchModules({ paginate }),
          this.fetchSites({ paginate }),
          this.fetchLanguages({ paginate }),
          this.fetchPageTypes({ paginate }),
          this.fetchLanguageSites({ paginate }),
          this.fetchAuthors(),
        ])
        await this.fetchPage()
        await this.fetchSimilarPages()
        await this.fetchEntityTypes()
        this.fetchWordpressData()
        this.setPageLoading(false)
        this.setDefaultPanels()
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to load page editor',
          duration: -1,
          data: { error },
        })
      }
    },
    setDefaultPanels() {
      this.panels = this.expanded
        ? this.filteredModules
            .map(({ fields = [] } = {}, index) => (fields.length ? index : null))
            .filter(index => index !== null)
        : []
    },
    async fetchPage() {
      try {
        this.formData = await pages.get(this.$route.params.id)
        this.formData.content = this.mapContent(this.formData.content)
        this.formData.published_content = this.mapContent(this.formData.published_content)
        this.original = cloneDeep({ ...this.formData })
      } catch (error) {
        this.formData = blankFormData()
        this.original = blankFormData()
        this.$_redirectHandler_404({ name: 'content.pages' })
      }
    },
    async deletePage() {
      await pages.delete(this.$route.params.id)
      this.toggleDeleteModal()
      this.$router.push('/content/pages')
    },
    onBeforeUnload(event) {
      if (!this.hasChanges) return
      this.confirmUnload(event)
    },
    onSaveModalCancel() {
      this.showSaveModal = false
      this.showOverwriteModal = false
      this.syncRevisions = false
    },
    onOverwriteClick() {
      this.syncRevisions = true
      this.showOverwriteModal = true
    },
    onResetConfirm() {
      this.formData = cloneDeep({ ...this.original })
      this.showResetModal = false
    },
    async onClearRevisionConfirm() {
      this.loading = true
      try {
        // Transform blank strings to null
        const transformedData = Object.fromEntries(
          Object.entries(this.formData).map(([key, value]) => [key, value === '' ? null : value])
        )
        const revertedData = {
          ...transformedData,
          content: transformedData.published_content,
          meta: transformedData.published_meta,
        }

        await pages.update(this.formData.id, { ...revertedData, sync_revisions: true })
        this.$notify({ type: 'success', text: 'Revised changes were successfully cleared' })

        this.showClearRevisionModal = false

        await this.fetchPage()
        await this.fetchSimilarPages()
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'There was an error resetting revision changes',
          duration: -1,
          data: { error },
        })
      }
      this.loading = false
    },
    async onSyncRevision() {
      this.syncRevisions = true
      await this.savePage()
    },
    async savePage() {
      this.$refs.form.validate()
      this.loading = true
      try {
        // Transform blank strings to null
        const transformedData = Object.fromEntries(
          Object.entries(this.formData).map(([key, value]) => [key, value === '' ? null : value])
        )

        await pages.update(this.formData.id, { ...transformedData, sync_revisions: this.syncRevisions })
        this.$notify({ type: 'success', text: 'The page was successfully saved.' })

        this.showSaveModal = false
        this.showOverwriteModal = false
        this.syncRevisions = false

        await this.fetchPage()
        await this.fetchSimilarPages()
      } catch (error) {
        this.$notify({ type: 'error', text: 'There was an error saving this page', duration: -1, data: { error } })
      }
      this.loading = false
    },
  },
}
</script>

<style scoped>
.container {
  max-width: 1280px;
}
</style>
